import React, { useState } from "react";
import { formatNZDate, formatNZDateTime } from "../../utils/timezone";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Zap, Eye, Trash2, DollarSign, CheckCircle, Star, Package, Clock, MapPin, FileText, Car, RotateCw } from "lucide-react";
import { AdminVehicleBackfillPanel } from "./AdminVehicleBackfillPanel";
import { AdminBulkRadiusResetPanel, AdminRestartRequestDialog } from "./AdminRequestAdminActions";

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-600 border-green-500/30",
  completed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  expired: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  paid: "bg-green-500/10 text-green-600 border-green-500/30",
  confirmed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  deleted: "bg-red-500/10 text-red-600 border-red-500/30",
  pending_fitment: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  closed: "bg-gray-500/10 text-gray-600 border-gray-500/30"
};

const PRICE_RANGE_LABELS = {
  budget: "Budget",
  lower: "Budget",
  mid: "Mid-Range",
  premium: "Premium",
  any: "Any Price Range",
  winter: "Winter Tyres",
  all_season: "All Season",
  ev: "EV Tyres"
};

export function AdminRequestsTab({
  requests,
  onViewRequest,
  onDeleteRequest,
  deletionRequests = [],
  onApproveDeletion,
  onRejectDeletion,
  onDataChanged,
}) {
  const [restartTarget, setRestartTarget] = useState(null);

  return (
    <div className="space-y-6">
      {/* Pending Deletion Requests */}
      {deletionRequests.length > 0 && (
        <Card className="border-red-300 dark:border-red-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Pending Deletion Requests ({deletionRequests.length})
            </CardTitle>
            <CardDescription>Shops have requested to delete these quotes — review and approve or reject</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {deletionRequests.map((q) => (
                <div key={q.quote_id} className="p-4 rounded-lg border border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20" data-testid={`deletion-request-${q.quote_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">Quote {q.quote_number || `#${q.quote_id?.substring(0, 8)}`}</span>
                        <span className="text-muted-foreground">for Request {q.request_number || `#${q.request_id}`}</span>
                        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">Deletion Requested</Badge>
                      </div>
                      <p className="text-sm"><span className="text-muted-foreground">Shop:</span> {q.shop_name || 'Unknown'}</p>
                      <p className="text-sm"><span className="text-muted-foreground">Quote:</span> {q.tyre_brand} {q.tyre_model} — ${q.total_price?.toFixed(2)}</p>
                      <div className="mt-2 p-2 bg-red-100/50 dark:bg-red-950/30 rounded border border-red-200 dark:border-red-800">
                        <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-0.5">Reason</p>
                        <p className="text-sm">{q.deletion_reason}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Requested: {formatNZDateTime(q.deletion_requested_at)}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="destructive" onClick={() => onApproveDeletion(q.quote_id)} data-testid={`approve-deletion-${q.quote_id}`}>
                        <CheckCircle className="h-4 w-4 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onRejectDeletion(q.quote_id)} data-testid={`reject-deletion-${q.quote_id}`}>
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    <Card className="glass-card">
      <CardHeader>
        <CardTitle>All Requests</CardTitle>
        <CardDescription>Quote requests across the platform — displayed as shops see them</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <AdminBulkRadiusResetPanel onCompleted={onDataChanged} />
          <AdminVehicleBackfillPanel />
          {requests.slice(0, 50).map((request) => (
            <Card key={request.request_id} className="border" data-testid={`request-row-${request.request_id}`}>
              <CardContent className="p-5">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    {/* Header row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-lg font-semibold">{request.request_number || `#${request.request_id}`}</h3>
                      {request.request_type === "urgent" && (
                        <Badge className="bg-primary/10 text-primary border-primary/30">
                          <Zap className="h-3 w-3 mr-1" />Urgent
                        </Badge>
                      )}
                      <Badge className={STATUS_COLORS[request.request_status]}>{request.request_status}</Badge>
                      {request.quotes_count > 0 && (
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">{request.quotes_count} quote{request.quotes_count !== 1 ? 's' : ''}</Badge>
                      )}
                    </div>

                    {/* Details row */}
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        {request.service_type?.replace(/_/g, ' ')}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {typeof request.address === 'object' ? request.address?.full_address?.substring(0, 40) : request.address?.substring(0, 40) || '-'}
                      </span>
                      <span>{request.search_radius}km radius</span>
                      {(request.vehicle_info?.rego || request.rego) && (
                        <span className="flex items-center gap-1">
                          <Car className="h-4 w-4" />
                          Rego: {request.vehicle_info?.rego || request.rego}
                        </span>
                      )}
                      {request.quantity && (request.service_type?.includes("tyres") || request.selected_services?.includes("tyres")) && (
                        <span>{request.quantity} tyres</span>
                      )}
                    </div>

                    {/* Tyre specs */}
                    {request.tyre_specs && request.tyre_specs.width && (
                      <p className="text-sm">
                        Tyre size: {request.tyre_specs.width}/{request.tyre_specs.aspect_ratio || request.tyre_specs.profile || "—"}R{request.tyre_specs.rim_size || "—"}
                      </p>
                    )}

                    {/* Brand & price preferences */}
                    <div className="flex flex-wrap gap-3 text-sm">
                      {request.brand_preference && (
                        <span className="text-muted-foreground">Preferred: {request.brand_preference}</span>
                      )}
                      {request.price_range && (
                        <span className="text-muted-foreground">Price Range: {PRICE_RANGE_LABELS[request.price_range] || request.price_range}</span>
                      )}
                    </div>

                    {/* Price range from quotes */}
                    {request.lowest_price != null && (
                      <p className="text-sm font-medium text-primary" data-testid={`request-price-${request.request_id}`}>
                        Quoted: ${request.lowest_price?.toFixed(0)}{request.highest_price !== request.lowest_price ? ` - $${request.highest_price?.toFixed(0)}` : ''}
                      </p>
                    )}

                    {/* Customer note */}
                    {request.customer_note && (
                      <div className="mt-1 p-2 bg-blue-500/5 border border-blue-500/20 rounded-md">
                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-0.5">Customer Note</p>
                        <p className="text-sm">{request.customer_note}</p>
                      </div>
                    )}
                  </div>

                  {/* Right side: date + actions */}
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <p className="text-xs text-muted-foreground">{formatNZDate(request.created_at)}</p>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" data-testid={`view-request-${request.request_id}`} onClick={() => onViewRequest(request.request_id)}>
                        <Eye className="h-4 w-4 mr-1" />View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRestartTarget(request)}
                        data-testid={`restart-request-${request.request_id}`}
                        title="Extend expiry & re-email shops"
                      >
                        <RotateCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive" data-testid={`delete-request-${request.request_id}`} onClick={() => onDeleteRequest(request)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>

    <AdminRestartRequestDialog
      open={!!restartTarget}
      request={restartTarget}
      onClose={() => setRestartTarget(null)}
      onCompleted={onDataChanged}
    />
    </div>
  );
}

export function AdminRequestDetailDialog({ dialog, onClose }) {
  const { open, data, loading } = dialog;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Request Detail
            {data && (
              <>
                <span className="text-primary">{data.request_number || `#${data.request_id}`}</span>
                {data.request_type === "urgent" && (
                  <Badge className="bg-primary/10 text-primary"><Zap className="h-3 w-3 mr-1" />Urgent</Badge>
                )}
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
        ) : data && (
          <div className="space-y-4">
            {/* Request Info */}
            <div className="grid grid-cols-2 gap-3 text-sm bg-muted/50 rounded-lg p-4">
              <div>
                <p className="text-muted-foreground">Customer</p>
                <p className="font-medium">{data.customer_name}</p>
                <p className="text-xs text-muted-foreground">{data.customer_email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <Badge className={STATUS_COLORS[data.request_status]}>{data.request_status}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Service</p>
                <p className="font-medium">{data.service_type?.replace(/_/g, ' ')}</p>
                {data.selected_services?.length > 0 && (
                  <p className="text-xs">{data.selected_services.join(', ')}</p>
                )}
              </div>
              <div>
                <p className="text-muted-foreground">Quantity</p>
                <p className="font-medium">{data.quantity || '-'} tyres</p>
              </div>
              <div>
                <p className="text-muted-foreground">Location</p>
                <p className="font-medium text-xs">{typeof data.address === 'object' ? data.address?.full_address : data.address || '-'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Search Radius</p>
                <p className="font-medium">{data.search_radius}km</p>
              </div>
              {data.vehicle_info?.rego && (
                <div>
                  <p className="text-muted-foreground">Rego</p>
                  <p className="font-medium">{data.vehicle_info.rego}</p>
                </div>
              )}
              {data.tyre_specs && (
                <div>
                  <p className="text-muted-foreground">Tyre Specs</p>
                  <p className="font-medium">{data.tyre_specs.width}/{data.tyre_specs.profile}R{data.tyre_specs.rim_size}</p>
                </div>
              )}
              {data.brand_preference && (
                <div>
                  <p className="text-muted-foreground">Brand Preference</p>
                  <p className="font-medium">{data.brand_preference}</p>
                </div>
              )}
              {data.price_range && (
                <div>
                  <p className="text-muted-foreground">Price Range</p>
                  <p className="font-medium capitalize">{data.price_range}</p>
                </div>
              )}
              {data.customer_note && (
                <div className="col-span-2">
                  <p className="text-muted-foreground">Customer Note</p>
                  <p className="font-medium">{data.customer_note}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Created</p>
                <p className="font-medium text-xs">{formatNZDateTime(data.created_at)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Time Remaining</p>
                <p className="font-medium">
                  {data.time_remaining?.expired ? (
                    <span className="text-destructive">Expired</span>
                  ) : (
                    `${data.time_remaining?.days || 0}d ${data.time_remaining?.hours || 0}h ${data.time_remaining?.minutes || 0}m`
                  )}
                </p>
              </div>
            </div>

            {/* Quotes Section */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Submitted Quotes ({data.quotes?.length || 0})
              </h4>
              {data.quotes?.length > 0 ? (
                <div className="space-y-3">
                  {data.quotes.map((quote) => (
                    <div key={quote.quote_id} className={`p-4 rounded-lg border ${data.accepted_quote_id === quote.quote_id ? 'bg-green-50 dark:bg-green-950/20 border-green-300' : 'bg-muted/30'}`} data-testid={`detail-quote-${quote.quote_id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{quote.shop_name || 'Unknown Shop'}</p>
                            {data.accepted_quote_id === quote.quote_id && (
                              <Badge className="bg-green-500/10 text-green-600"><CheckCircle className="h-3 w-3 mr-1" />Accepted</Badge>
                            )}
                            {quote.stock_availability && (
                              <Badge variant="outline" className="text-xs">{quote.stock_availability?.replace(/_/g, ' ')}</Badge>
                            )}
                          </div>
                          {quote.shop_phone && <p className="text-xs text-muted-foreground mt-0.5">{quote.shop_phone}</p>}
                          <div className="mt-2 text-sm space-y-0.5">
                            {quote.tyre_brand && <p>{quote.tyre_brand} {quote.tyre_model}{quote.tyre_size ? ` - ${quote.tyre_size}` : ''}{quote.speed_rating ? ` (${quote.speed_rating})` : ''}</p>}
                            {quote.fitment_estimate && <p className="text-muted-foreground">Est. Fitment: {quote.fitment_estimate}</p>}
                            {quote.distance_km != null && quote.distance_km < 9999 && <p className="text-muted-foreground">{quote.distance_km?.toFixed(1)}km away</p>}
                            {quote.notes && <p className="text-muted-foreground italic mt-1">"{quote.notes}"</p>}
                          </div>
                          {quote.google_rating != null && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                              <span className="text-xs font-medium">{quote.google_rating?.toFixed(1)}</span>
                              {quote.google_review_count != null && <span className="text-xs text-muted-foreground">({quote.google_review_count})</span>}
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          {(() => {
                            const pb = quote.price_breakdown || {};
                            const inclFactor = pb.prices_include_gst ? 1 : 1.15;
                            const tyreFitmentRaw = pb.tyre_fitment || pb.tyres || 0;
                            const tyreFitmentInclGst = tyreFitmentRaw * inclFactor;
                            const alignmentInclGst = (pb.alignment || 0) * inclFactor;
                            const rotationInclGst = (pb.rotation || 0) * inclFactor;
                            const totalInclGst = quote.total_price || 0;
                            const qty = data.quantity || 4;
                            const isTyreQuote =
                              (data.service_type?.includes("tyres") ||
                                data.selected_services?.includes("tyres")) &&
                              tyreFitmentInclGst > 0 && qty > 0;
                            const perTyreInclGst = isTyreQuote ? tyreFitmentInclGst / qty : 0;
                            const perTyreExclGst = perTyreInclGst / 1.15;
                            return (
                              <>
                                {isTyreQuote ? (
                                  <>
                                    <p className="text-2xl font-bold text-primary leading-tight" data-testid={`detail-per-tyre-excl-gst-${quote.quote_id}`}>
                                      ${perTyreExclGst.toFixed(2)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground mb-1.5">excl. GST per tyre fitted</p>
                                    <p className="text-sm font-semibold text-foreground/80 leading-tight" data-testid={`detail-per-tyre-incl-gst-${quote.quote_id}`}>
                                      ${perTyreInclGst.toFixed(2)}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground">incl. GST per tyre fitted</p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-2xl font-bold text-primary" data-testid={`detail-quote-price-${quote.quote_id}`}>
                                      ${totalInclGst.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">incl. GST</p>
                                  </>
                                )}

                                {/* Breakdown - intentionally less prominent (matches customer view) */}
                                <div className="text-xs text-muted-foreground mt-2 space-y-1 border-t pt-2 text-left min-w-[180px]">
                                  {tyreFitmentInclGst > 0 && (
                                    <div className="flex justify-between gap-4">
                                      <span>Total fitment:</span>
                                      <span>${tyreFitmentInclGst.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {alignmentInclGst > 0 && (
                                    <div className="flex justify-between gap-4">
                                      <span>Tyre alignment:</span>
                                      <span>${alignmentInclGst.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {rotationInclGst > 0 && (
                                    <div className="flex justify-between gap-4">
                                      <span>Tyre rotation:</span>
                                      <span>${rotationInclGst.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between gap-4 pt-1 mt-1 border-t font-medium text-foreground">
                                    <span>Total payable:</span>
                                    <span data-testid={`detail-total-payable-${quote.quote_id}`}>
                                      ${totalInclGst.toFixed(2)}
                                    </span>
                                  </div>
                                  {isTyreQuote && (
                                    <p className="text-[10px] text-muted-foreground/80 pt-1 italic">
                                      All prices include GST. Per-tyre fitted shown is for {qty} tyres.
                                    </p>
                                  )}
                                </div>
                              </>
                            );
                          })()}

                          <p className="text-xs text-muted-foreground mt-1">{formatNZDate(quote.created_at)}</p>
                        </div>
                      </div>
                      {quote.inclusions?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">Includes: {quote.inclusions.join(', ')}</p>
                      )}
                      {/* Included Services badges */}
                      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t">
                        {(data.service_type?.includes("tyres") || data.selected_services?.includes("tyres")) && (
                          <>
                            <Badge variant="secondary" className="text-xs">Tyres</Badge>
                            <Badge variant="secondary" className="text-xs">Fitting & Balancing</Badge>
                            <Badge variant="secondary" className="text-xs">Tyre Stewardship</Badge>
                          </>
                        )}
                        {(data.service_type?.includes("alignment") || data.selected_services?.includes("alignment")) && (
                          <Badge variant="secondary" className="text-xs">Wheel Alignment</Badge>
                        )}
                        {(data.service_type === "rotation_only" || data.service_type?.includes("rotation") || data.selected_services?.includes("rotation")) && (
                          <Badge variant="secondary" className="text-xs">Tyre Rotation</Badge>
                        )}
                        <Badge variant="outline" className="text-xs text-muted-foreground">GST Included</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No quotes submitted yet</p>
              )}
            </div>

            {/* Order Info */}
            {data.order && (
              <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-300">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Order #{data.order.order_id?.substring(0, 8)}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Amount:</span> <span className="font-medium">${data.order.order_amount?.toFixed(2)}</span></div>
                  <div><span className="text-muted-foreground">Status:</span> <Badge className={STATUS_COLORS[data.order.order_status]}>{data.order.order_status}</Badge></div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
